/**
 * ScopeBuilderModal — pick which quoted systems + warranty tiers appear on
 * the scope PDF, with a live PDF preview. Selections persist on the deal
 * (scope_include) so re-downloads and emails reproduce the same document.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SLOTS = [
  { field: "proposal_option_25yr", years: 25 },
  { field: "proposal_option_1",    years: 20 },
  { field: "proposal_option_2",    years: 15 },
  { field: "proposal_option_3",    years: 10 },
];
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export const dealHasQuotedSystems = (deal) =>
  SLOTS.some((s) => (deal?.[`${s.field}_system_id`] || "").trim());

const yearsOf = (deal, s) => {
  const m = /^(\d+)\s*-\s*Year/i.exec(deal?.[`${s.field}_label`] || "");
  return m ? Number(m[1]) : s.years;
};

const PhotoThumb = ({ dealId, photoId }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    api.get(`/projects/${dealId}/photos/${photoId}/download?thumb=1&max_size=200`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [dealId, photoId]);
  return src
    ? <img src={src} alt="" className="w-full h-full object-cover" />
    : <div className="w-full h-full bg-zinc-200 animate-pulse" />;
};

const W_FIELD = { 25: "warranty_25yr_add", 20: "warranty_20yr_add", 15: "warranty_15yr_add", 10: "warranty_10yr_add" };

export const ScopeBuilderModal = ({ open, onOpenChange, deal, onConfirm }) => {
  const quoted = SLOTS.filter((s) => (deal?.[`${s.field}_system_id`] || "").trim());
  const [fields, setFields] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [coverId, setCoverId] = useState(null);
  const [coverBump, setCoverBump] = useState(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open || !deal) return;
    const saved = deal.scope_include || {};
    const f = (saved.option_fields || []).filter((x) => quoted.some((s) => s.field === x));
    const initial = f.length ? f : quoted.map((s) => s.field);
    setFields(initial);
    const autoYears = [...new Set(quoted.filter((s) => initial.includes(s.field)).map((s) => yearsOf(deal, s)))];
    setTiers(saved.warranty_years?.length ? saved.warranty_years : autoYears);
    setCustomItems((saved.custom_lines || []).map((x) =>
      typeof x === "string" ? { text: x, amount: "" } : { text: x.text || "", amount: x.amount > 0 ? String(x.amount) : "" }
    ));
    setMilestones((deal.payment_milestones || []).map((m) => ({
      ref: m, label: m.label || "", percent: m.percent > 0 ? String(m.percent) : "", due: m.due_date || "",
    })));
    api.get(`/projects/${deal.id}/photos`).then((r) => {
      const list = (r.data || []).slice(0, 24);
      setPhotos(list);
      setCoverId(list.find((p) => p.is_cover)?.id || null);
    }).catch(() => setPhotos([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickCover = async (p) => {
    if (p.id === coverId) return;
    setCoverId(p.id);
    try {
      await api.patch(`/projects/${deal.id}/photos/${p.id}`, { is_cover: true });
      if (deal.cover_photo_file_id) {
        // Clear the legacy deal-level pointer so the starred photo governs the cover
        await api.put(`/deals/${deal.id}`, { ...deal, cover_photo_file_id: "" });
        deal.cover_photo_file_id = "";
      }
      setCoverBump((n) => n + 1); // refresh the live preview
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set cover photo");
    }
  };

  const customPayload = () => customItems
    .map((r) => ({ text: (r.text || "").trim(), amount: Number(r.amount) || 0 }))
    .filter((r) => r.text);

  const msPayload = () => milestones
    .filter((r) => (r.label || "").trim())
    .map((r) => ({ ...(r.ref || {}), label: r.label.trim(), percent: Number(r.percent) || 0, due_date: r.due || "" }));

  const msTotal = milestones.reduce((s, r) => s + (Number(r.percent) || 0), 0);

  // Live preview — debounced re-fetch whenever the selections change.
  useEffect(() => {
    if (!open || !deal || !fields.length) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post(
          `/deals/${deal.id}/spec-sheet/preview`,
          { option_fields: fields, warranty_years: tiers, custom_lines: customPayload(), payment_schedule: msPayload() },
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setPreviewUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
      } catch {
        // preview failure is non-blocking — Generate still works
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [open, fields, tiers, customItems, milestones, coverBump]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleField = (s) => {
    setFields((prev) => {
      const next = prev.includes(s.field) ? prev.filter((x) => x !== s.field) : [...prev, s.field];
      setTiers([...new Set(quoted.filter((q) => next.includes(q.field)).map((q) => yearsOf(deal, q)))]);
      return next;
    });
  };

  const go = async () => {
    if (!fields.length) { toast.warning("Pick at least one system"); return; }
    setBusy(true);
    try {
      await api.put(`/deals/${deal.id}`, { ...deal, scope_include: { option_fields: fields, warranty_years: tiers, custom_lines: customPayload() }, payment_milestones: msPayload() });
      onOpenChange(false);
      onConfirm?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save scope selections");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight">Build Scope</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5">
          <div className="space-y-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Systems on this scope</div>
              {quoted.map((s) => (
                <label key={s.field} className="flex items-center gap-2.5 py-1.5 text-sm cursor-pointer" data-testid={`scope-opt-${s.field}`}>
                  <input type="checkbox" checked={fields.includes(s.field)} onChange={() => toggleField(s)} />
                  <span className="font-bold flex-1">{(deal?.[`${s.field}_label`] || `${s.years}-Year Option`).replace(" — Standard Warranty", "")}</span>
                  <span className="font-mono text-xs text-zinc-500">{fmt(deal?.[s.field])}</span>
                </label>
              ))}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Optional manufacturer warranty shown</div>
              {quoted.filter((s) => fields.includes(s.field)).map((s) => {
                const y = yearsOf(deal, s);
                const perSlot = Number(deal?.[`${s.field}_warranty_add`] || 0);
                const amt = perSlot > 0 ? perSlot : Number(deal?.[W_FIELD[y]] || 0);
                const wLabel = `${(deal?.[`${s.field}_label`] || `${y}-Year Option`).replace(/\s*—.*$/, "")} System`;
                return (
                  <label key={s.field} className="flex items-center gap-2.5 py-1.5 text-sm cursor-pointer" data-testid={`scope-tier-${s.field}`}>
                    <input type="checkbox" checked={tiers.includes(y)} onChange={() => setTiers((p) => p.includes(y) ? p.filter((x) => x !== y) : [...p, y])} />
                    <span className="font-bold flex-1">{wLabel}</span>
                    <span className="font-mono text-xs text-zinc-500">{amt > 0 ? fmt(amt) : "no $ set"}</span>
                  </label>
                );
              })}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Custom one-off items</div>
              <div className="space-y-1.5">
                {customItems.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_86px_22px] gap-1.5 items-center">
                    <input
                      value={row.text}
                      onChange={(e) => setCustomItems((p) => p.map((r, i) => i === idx ? { ...r, text: e.target.value } : r))}
                      placeholder="e.g. Replace 4 damaged skylight curbs"
                      data-testid={`scope-custom-text-${idx}`}
                      className="border border-zinc-300 px-2 h-8 text-xs focus:outline-none focus:border-blue-700"
                    />
                    <div className="relative">
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 pointer-events-none">$</span>
                      <input
                        type="number" min="0" step="0.5"
                        value={row.amount}
                        onChange={(e) => setCustomItems((p) => p.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                        placeholder="0"
                        data-testid={`scope-custom-amount-${idx}`}
                        className="border border-zinc-300 pl-4 pr-1 h-8 text-xs font-mono w-full focus:outline-none focus:border-blue-700"
                      />
                    </div>
                    <button onClick={() => setCustomItems((p) => p.filter((_, i) => i !== idx))} data-testid={`scope-custom-remove-${idx}`} className="text-zinc-400 hover:text-red-600 text-sm leading-none">×</button>
                  </div>
                ))}
                <button
                  onClick={() => setCustomItems((p) => [...p, { text: "", amount: "" }])}
                  data-testid="scope-custom-add"
                  className="text-[11px] font-bold uppercase tracking-wider text-blue-700 hover:underline"
                >
                  + Add item
                </button>
              </div>
              <div className="text-[10px] text-zinc-400 mt-1">Prints between the flashing bullet and the systems lead-in. Prices stay hidden on the scope and roll into every option's total.</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Payment milestone schedule</div>
              <div className="space-y-1.5">
                {milestones.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[1fr_64px_112px_22px] gap-1.5 items-center">
                    <input
                      value={row.label}
                      onChange={(e) => setMilestones((p) => p.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                      placeholder="e.g. Upon acceptance / materials"
                      data-testid={`scope-milestone-label-${idx}`}
                      className="border border-zinc-300 px-2 h-8 text-xs focus:outline-none focus:border-blue-700"
                    />
                    <div className="relative">
                      <input
                        type="number" min="0" max="100" step="5"
                        value={row.percent}
                        onChange={(e) => setMilestones((p) => p.map((r, i) => i === idx ? { ...r, percent: e.target.value } : r))}
                        placeholder="0"
                        data-testid={`scope-milestone-pct-${idx}`}
                        className="border border-zinc-300 pl-2 pr-5 h-8 text-xs font-mono w-full focus:outline-none focus:border-blue-700"
                      />
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 pointer-events-none">%</span>
                    </div>
                    <input
                      type="date"
                      value={row.due}
                      onChange={(e) => setMilestones((p) => p.map((r, i) => i === idx ? { ...r, due: e.target.value } : r))}
                      title="Target date — prints on the scope and shows on the calendar"
                      data-testid={`scope-milestone-due-${idx}`}
                      className="border border-zinc-300 px-1.5 h-8 text-[11px] focus:outline-none focus:border-blue-700"
                    />
                    <button onClick={() => setMilestones((p) => p.filter((_, i) => i !== idx))} data-testid={`scope-milestone-remove-${idx}`} className="text-zinc-400 hover:text-red-600 text-sm leading-none">×</button>
                  </div>
                ))}
                <button
                  onClick={() => setMilestones((p) => [...p, { ref: null, label: "", percent: "", due: "" }])}
                  data-testid="scope-milestone-add"
                  className="text-[11px] font-bold uppercase tracking-wider text-blue-700 hover:underline"
                >
                  + Add milestone
                </button>
              </div>
              <div className={`text-[10px] mt-1 ${milestones.length && Math.round(msTotal) !== 100 ? "text-amber-600 font-bold" : "text-zinc-400"}`} data-testid="scope-milestone-total">
                {milestones.length
                  ? `Total: ${msTotal}%${Math.round(msTotal) !== 100 ? " — should equal 100%" : ""} · prints under Payment Terms`
                  : "Empty = standard 50% on acceptance / balance at completion wording."}
              </div>
            </div>
            {photos.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Cover photo</div>
                <div className="grid grid-cols-4 gap-1.5 max-h-[132px] overflow-y-auto pr-1" data-testid="scope-cover-picker">
                  {photos.map((p, idx) => (
                    <button
                      key={p.id}
                      onClick={() => pickCover(p)}
                      data-testid={`scope-cover-photo-${idx}`}
                      title={p.label || p.original_filename || "Photo"}
                      className={`relative aspect-square overflow-hidden rounded-sm border-2 transition-colors ${coverId === p.id ? "border-blue-700" : "border-transparent hover:border-zinc-300"}`}
                    >
                      <PhotoThumb dealId={deal.id} photoId={p.id} />
                      {coverId === p.id && (
                        <span className="absolute bottom-0 inset-x-0 bg-blue-700 text-white text-[8px] font-bold uppercase tracking-wider text-center leading-4">Cover</span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-zinc-400 mt-1">Appears full-bleed on the scope cover page.</div>
              </div>
            )}
            <div className="flex items-center gap-2 pt-3 border-t border-zinc-200">
              <button onClick={go} disabled={busy} data-testid="scope-builder-generate" className="inline-flex items-center gap-2 h-10 px-5 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-800 rounded-sm disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Generate Scope
              </button>
              <button onClick={() => onOpenChange(false)} className="h-10 px-3 text-xs text-zinc-500 hover:text-zinc-950">Cancel</button>
            </div>
          </div>
          <div className="relative border border-zinc-200 rounded-sm bg-zinc-100 min-h-[60vh]" data-testid="scope-preview-pane">
            {previewUrl ? (
              <iframe title="Scope preview" src={`${previewUrl}#toolbar=0&navpanes=0`} className="w-full h-[60vh]" data-testid="scope-preview-iframe" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-400 font-bold uppercase tracking-wider">
                {fields.length ? "Loading preview…" : "Pick at least one system"}
              </div>
            )}
            {previewLoading && (
              <div className="absolute top-2 right-2 bg-white/90 border border-zinc-200 rounded-sm px-2 py-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500" data-testid="scope-preview-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Updating
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
